// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Skeleton from "@eisinc/App/Components/Skeleton/Skeleton";
import { setFilterDrawerIsOpen } from "@eisinc/App/Handlers/XNG/ProductList";
import { pluralize } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => {
    const pagination = getProductListDataViewProperty(state, "pagination");
    const correctedQuery = getProductListDataViewProperty(state, "correctedQuery");
    const { productFilters } = state.pages.productList;

    return {
        totalItemCount: pagination?.totalItemCount,
        query: correctedQuery || productFilters.query,
        isEstimatedItemCount: getSettingsCollection(state).searchSettings.searchProvider === "Commerce Search v3",
        // XNG-Changes
        isLoading: state.pages.productList.isLoading,
    };
};

// <XNG-Changes>
const mapDispatchToProps = {
    setFilterDrawerIsOpen,
};
// </XNG-Changes>

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListProductCountStyles {
    wrapper?: InjectableCss;
    countText?: TypographyPresentationProps;
    itemsText?: TypographyPresentationProps;
    queryText?: TypographyPresentationProps;
    // XNG-Changes
    countAndFilterWrapper?: InjectableCss;
    filter?: LinkPresentationProps;
}

export const productCountStyles: ProductListProductCountStyles = {
    wrapper: {
        css: css`
            margin-top: 10px;
        `,
    },
    countText: {
        css: css`
            white-space: nowrap;
        `,
    },
    queryText: {
        css: css`
            white-space: nowrap;
            font-weight: bold;
        `,
    },
};

const styles = productCountStyles;

const ProductListProductCount: FC<Props> = ({
    totalItemCount,
    query,
    isEstimatedItemCount,
    // XNG-Changes
    isLoading,
    setFilterDrawerIsOpen,
}) => {
    // <XNG-Changes>
    if (isLoading) {
        return (
            <StyledWrapper {...styles.countAndFilterWrapper}>
                <Skeleton isLoading={true} height="24px" width="100%" />
            </StyledWrapper>
        );
    }

    // </XNG-Changes>
    // if (!totalItemCount) {
    //     return null;
    // }

    // <XNG-Changes>
    const handleOpenFilterDrawer = () => setFilterDrawerIsOpen({ filterDrawerIsOpen: true });
    // </XNG-Changes>

    // XNG-Changes : use pluralize instead
    // const itemsText = (totalItemCount !== 1 ? " items" : " item") + (query ? " for " : "");

    return (
        // XNG-Changes: add StyledWrapper and add Filter Link
        <StyledWrapper {...styles.countAndFilterWrapper} {...{ totalItemCount }}>
            {!!totalItemCount && (
                <StyledWrapper {...styles.wrapper}>
                    {/* <Typography {...styles.countText} data-test-selector="productListCountText">
                    {totalItemCount}
                </Typography> */}
                    {/* XNG-Changes: EIS-4540 - forwardAs h1 for SRP */}
                    <Typography {...styles.itemsText} forwardAs={query ? "h1" : "span"}>
                        {
                            // XNG-Changes
                            // translate(itemsText)
                            pluralize(totalItemCount || 0, "Item", { showNumber: true })
                        }
                        {isEstimatedItemCount ? " (estimated)" : ""}
                        {/* <XNG-Changes> */}
                        {query && (
                            <Typography {...styles.queryText}>
                                &nbsp;{translate("for")}&nbsp;
                                <Typography data-test-selector="productListQueryText">{query}</Typography>
                            </Typography>
                        )}
                        {/* </XNG-Changes> */}
                    </Typography>
                    {/* {query && (
                    <Typography {...styles.queryText} data-test-selector="productListQueryText">
                        {query}
                    </Typography>
                )} */}
                </StyledWrapper>
            )}
            <Link
                aria-label="open filter drawer"
                data-test-selector="filter-drawer-trigger"
                {...styles.filter}
                onClick={handleOpenFilterDrawer}
            >
                {translate("Filter Results")}
            </Link>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListProductCount),
    definition: {
        group: "Product List",
        displayName: "Product List Count",
        // XNG-Changes
        allowedContexts: ["ProductListPage", "CategoryDetailsPage"],
    },
};

export default widgetModule;
